import React, { useState, useEffect } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

const t = (key: string) => window.i18n.t(key)

const filterVariants = cva("collapsable-filter", {
  variants: {
    locked: {
      true: "disabled",
    },
  },
  defaultVariants: {
    locked: false,
  },
})

export interface CollapsableFilterProps extends VariantProps<typeof filterVariants> {
  label: string
  icon: string
  tooltip?: string
  count?: number
  hasValues?: boolean
  onClear?: () => void
  isLocked?: boolean
  lockedTooltip?: string
  defaultExpanded?: boolean
  className?: string
  children: React.ReactNode
}

export function CollapsableFilter({
  label,
  icon,
  tooltip,
  count,
  hasValues = false,
  onClear,
  isLocked = false,
  lockedTooltip,
  defaultExpanded,
  className,
  children,
}: CollapsableFilterProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? hasValues)

  // Auto-collapse when values are cleared externally (e.g., Clear All)
  useEffect(() => {
    if (!hasValues && isExpanded) {
      setIsExpanded(false)
    }
  }, [hasValues])

  // Locked state render
  if (isLocked) {
    return (
      <div className={cn(filterVariants({ locked: true }), className)}>
        <div className="collapsable-filter__header">
          <span className={cn("collapsable-filter__icon", icon)} aria-hidden="true" />
          <div className="collapsable-filter__title">{label}</div>
          <button
            type="button"
            className="collapsable-filter__toggle btn-empty"
            aria-label={t("js.back.discover.filters.toggle")}
            data-bs-toggle="popover"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-container="body"
            data-bs-content={lockedTooltip}
          >
            <span className="far fa-lock" aria-hidden="true" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(filterVariants({ locked: false }), className)}>
      <div
        className="collapsable-filter__header"
        {...(count && count > 0 ? { "data-count": count } : {})}
      >
        <span className={cn("collapsable-filter__icon", icon)} aria-hidden="true" />
        <div className="collapsable-filter__title">
          {label}
          {tooltip && (
            <button
              type="button"
              className="collapsable-filter__hint question-circle"
              data-controller="tooltip"
              title={tooltip}
            >
              <span className="far fa-info-circle" aria-hidden="true" />
            </button>
          )}
        </div>
        {hasValues && onClear && (
          <button
            type="button"
            className="h-button h-button--link h-button--sm collapsable-filter__clear"
            onClick={onClear}
          >
            <strong>{t("js.back.discover.filters.clear")}</strong>
          </button>
        )}
        <button
          type="button"
          className="collapsable-filter__toggle btn-empty"
          aria-label={t("js.back.discover.filters.toggle")}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span
            className={`far fa-angle-${isExpanded ? "up" : "down"}`}
            aria-hidden="true"
          />
        </button>
      </div>

      {isExpanded && (
        <div className="collapsable-filter__content">
          {children}
        </div>
      )}
    </div>
  )
}
